'use client'

import type { ReactNode } from 'react'
import { Component, useState } from 'react'

import Button from '@/shared/ui/Button'

import S from './ErrorBoundary.module.scss'

interface ErrorFallbackProps {
  error: Error
  resetError: () => void
}

interface InternalErrorBoundaryProps {
  children: ReactNode
  fallback: (props: ErrorFallbackProps) => ReactNode
}

interface InternalErrorBoundaryState {
  error: Error | null
}

class InternalErrorBoundary extends Component<
  InternalErrorBoundaryProps,
  InternalErrorBoundaryState
> {
  public state: InternalErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): InternalErrorBoundaryState {
    return { error }
  }

  private readonly resetError = () => {
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) {
      return this.props.fallback({
        error: this.state.error,
        resetError: this.resetError
      })
    }

    return this.props.children
  }
}

interface AppErrorBoundaryProps {
  children: ReactNode
  onRetry?: () => void
}

export function AppErrorBoundary({ children, onRetry }: AppErrorBoundaryProps) {
  return (
    <InternalErrorBoundary
      fallback={({ resetError }) => (
        <div className={S.appFallback}>
          <h2 className={S.title}>Something went wrong</h2>
          <p className={S.description}>
            We could not render this page. Please try again.
          </p>

          <div className={S.actions}>
            <Button
              onClick={() => {
                onRetry?.()
                resetError()
              }}
              color="secondary"
              size="m"
            >
              Retry
            </Button>
          </div>
        </div>
      )}
    >
      {children}
    </InternalErrorBoundary>
  )
}

interface SectionErrorBoundaryProps {
  children: ReactNode
  title?: string
  description?: string
  retryLabel?: string
  onRetry?: () => void
}

export function SectionErrorBoundary({
  children,
  title = 'Section unavailable',
  description = 'This section failed to load. Try again.',
  retryLabel = 'Retry',
  onRetry
}: SectionErrorBoundaryProps) {
  const [retryKey, setRetryKey] = useState(0)

  return (
    <InternalErrorBoundary
      key={retryKey}
      fallback={({ resetError }) => (
        <div className={S.sectionFallback}>
          <h3 className={S.title}>{title}</h3>
          <p className={S.description}>{description}</p>
          <Button
            color="secondary"
            size="m"
            onClick={() => {
              onRetry?.()
              resetError()
              setRetryKey(value => value + 1)
            }}
          >
            {retryLabel}
          </Button>
        </div>
      )}
    >
      {children}
    </InternalErrorBoundary>
  )
}
